import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  BarChart3, Clock, DollarSign, Shield, Users, Briefcase,
  CalendarDays, GitCompareArrows, ArrowRight, Layers, Settings,
  Database, Link2, Server, Palette, TrendingUp, Download, Loader2, Mail
} from 'lucide-react';
import { exportScopePack } from '../../lib/factsheetPdf';
import { getCurrencySymbol } from '../../lib/currency';
import { ScopePackOptionsDialog } from '../../components/estimator/ScopePackOptionsDialog';
import { EmailScopePackDialog } from '../../components/estimator/EmailScopePackDialog';
import { ScopePackSendHistory } from '../../components/estimator/ScopePackSendHistory';
import { StakeholderFeedbackSummary } from '../../components/estimator/StakeholderFeedbackSummary';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { useAuth } from '../../context/AuthContext';
import LiveKPIRail from '../../components/LiveKPIRail';
import { DealCloseDateCard } from '../../components/estimator/DealCloseDateCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GROUP_ICONS = {
  'Consulting & Delivery': Briefcase,
  'Core Configuration': Settings,
  'Extended Configuration': Layers,
  'User Experience': Palette,
  'Integrations': Link2,
  'Data Migration': Database,
  'Infrastructure': Server,
  'Project Overhead': Shield,
};

const GROUP_COLORS = {
  'Consulting & Delivery': 'bg-blue-50 text-blue-700 border-blue-200',
  'Core Configuration': 'bg-slate-50 text-slate-700 border-slate-200',
  'Extended Configuration': 'bg-purple-50 text-purple-700 border-purple-200',
  'User Experience': 'bg-pink-50 text-pink-700 border-pink-200',
  'Integrations': 'bg-amber-50 text-amber-700 border-amber-200',
  'Data Migration': 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'Infrastructure': 'bg-indigo-50 text-indigo-700 border-indigo-200',
  'Project Overhead': 'bg-violet-50 text-violet-700 border-violet-200',
};

const MODIFIER_LABELS = {
  governance: 'Governance',
  content_readiness: 'Content Readiness',
  client_capability: 'Client Capability',
  change_readiness: 'Change Readiness',
  integration_complexity: 'Integration Complexity',
  org_complexity: 'Org Complexity',
  timeline_pressure: 'Timeline Pressure',
  data_integrity: 'Data Integrity',
};

export default function EstimateOverviewTab() {
  const { estimateId, estimate, reloadEstimate } = useOutletContext();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [allComponents, setAllComponents] = useState([]);
  const [scopeExporting, setScopeExporting] = useState(false);
  const [scopeDialogOpen, setScopeDialogOpen] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [sendHistoryKey, setSendHistoryKey] = useState(0);
  const [rfpDialogOpen, setRfpDialogOpen] = useState(false);
  const [rfpIncludeCosts, setRfpIncludeCosts] = useState(true);
  const [rfpExporting, setRfpExporting] = useState(false);
  const [rfpBlocks, setRfpBlocks] = useState(null); // null = not loaded
  const [rfpSelectedBlocks, setRfpSelectedBlocks] = useState([]);
  const [packTemplates, setPackTemplates] = useState([]);
  const [tplName, setTplName] = useState('');
  const [tplSaving, setTplSaving] = useState(false);
  const [rfpCover, setRfpCover] = useState({ client_name: '', rfp_reference: '', rep_contact: '' });
  const [rfpEmailOpen, setRfpEmailOpen] = useState(false);
  const [rfpEmail, setRfpEmail] = useState({ recipient_email: '', recipient_name: '', message: '' });
  const [rfpEmailing, setRfpEmailing] = useState(false);
  const [rfpSends, setRfpSends] = useState(null);

  const loadRfpSends = useCallback(() => {
    axios.get(`${API_URL}/api/rfp/support-pack/${estimateId}/sends`, { withCredentials: true })
      .then(res => setRfpSends(res.data?.sends || []))
      .catch(() => setRfpSends([]));
  }, [estimateId]);

  useEffect(() => {
    if (rfpDialogOpen && rfpBlocks === null) {
      axios.get(`${API_URL}/api/rfp/boilerplate`, { withCredentials: true })
        .then(res => setRfpBlocks(res.data?.blocks || []))
        .catch(() => setRfpBlocks([]));
      axios.get(`${API_URL}/api/rfp/pack-templates`, { withCredentials: true })
        .then(res => setPackTemplates(res.data?.templates || []))
        .catch(() => {});
    }
    if (rfpDialogOpen) {
      loadRfpSends();
      setRfpCover(prev => ({
        client_name: prev.client_name || estimate?.organization_name || '',
        rfp_reference: prev.rfp_reference,
        rep_contact: prev.rep_contact || [user?.name, user?.email].filter(Boolean).join(' · '),
      }));
    }
  }, [rfpDialogOpen, rfpBlocks, estimate, user, loadRfpSends]);

  const toggleRfpBlock = (id) => setRfpSelectedBlocks(prev =>
    prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const rfpPayload = () => ({
    include_costs: rfpIncludeCosts,
    boilerplate_ids: rfpSelectedBlocks,
    client_name: rfpCover.client_name || null,
    rfp_reference: rfpCover.rfp_reference || null,
    rep_contact: rfpCover.rep_contact || null,
  });

  const handleRfpEmail = async () => {
    if (!rfpEmail.recipient_email.includes('@')) { toast.error('Enter a valid recipient email'); return; }
    setRfpEmailing(true);
    toast.info('Building and emailing the RFP Support Pack — this can take up to a minute…');
    try {
      await axios.post(
        `${API_URL}/api/rfp/support-pack/${estimateId}/email`,
        { ...rfpPayload(), ...rfpEmail },
        { withCredentials: true, timeout: 180000 }
      );
      toast.success(`RFP Support Pack emailed to ${rfpEmail.recipient_email}`);
      setRfpEmail({ recipient_email: '', recipient_name: '', message: '' });
      loadRfpSends();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not email the RFP Support Pack');
    } finally {
      setRfpEmailing(false);
    }
  };

  const handleRfpPack = async () => {
    setRfpExporting(true);
    setRfpDialogOpen(false);
    toast.info('Building your RFP Support Pack — this can take up to a minute (AI description + charts)…');
    try {
      const res = await axios.post(
        `${API_URL}/api/rfp/support-pack/${estimateId}`,
        rfpPayload(),
        { withCredentials: true, responseType: 'blob', timeout: 180000 }
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `RFP_Support_Pack_${(estimate?.estimate_name || 'Project').replace(/[^\w\- ]/g, '').replace(/ /g, '_')}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('RFP Support Pack downloaded');
    } catch (err) {
      toast.error('Could not build the RFP Support Pack');
    } finally {
      setRfpExporting(false);
    }
  };

  useEffect(() => {
    axios.get(`${API_URL}/api/components`, { withCredentials: true })
      .then(res => setAllComponents(res.data?.components || []))
      .catch(() => setAllComponents([]));
  }, []);

  // Pinned KPIs for the right rail — derived from estimate.results.
  // Declared BEFORE the early return below so React hook order stays
  // consistent across renders.
  const kpiMetrics = useMemo(() => {
    const r = estimate?.results;
    if (!r) return [];
    const fmt = (n) => n?.toLocaleString(undefined, { maximumFractionDigits: 0 });
    const sym = getCurrencySymbol(estimate?.currency_code);
    const weeks = r.timing_meta?.calendar_span_weeks ?? r.timing_meta?.effort_based_weeks;
    const overrun = r.timing_meta?.schedule_overrun_weeks || 0;
    const conf = r.confidence;
    return [
      {
        label: 'Total Effort',
        value: r.total_effort != null ? `${fmt(r.total_effort)} hrs` : '—',
        hint: r.effort_range
          ? `${fmt(r.effort_range.low)}–${fmt(r.effort_range.high)} range`
          : null,
        accent: 'slate',
        testId: 'effort',
      },
      {
        label: 'Total Cost',
        value: r.total_cost != null ? `${sym}${fmt(r.total_cost)}` : '—',
        hint: r.cost_range
          ? `${sym}${fmt(r.cost_range.low)}–${sym}${fmt(r.cost_range.high)}`
          : null,
        accent: 'cyan',
        testId: 'cost',
      },
      {
        label: 'Duration',
        value: weeks != null ? `${weeks} wk` : '—',
        hint: r.timing_meta?.break_extension_weeks
          ? `+${r.timing_meta.break_extension_weeks}w break ext.`
          : null,
        accent: 'slate',
        testId: 'duration',
      },
      {
        label: 'Schedule Risk',
        value: overrun > 0 ? `+${overrun} wk overrun` : 'On time',
        hint: overrun > 0 ? 'Past committed go-live' : conf != null ? `${conf.toFixed(0)}% confidence` : null,
        accent: overrun > 0 ? 'rose' : 'emerald',
        testId: 'risk',
      },
    ];
  }, [estimate]);

  if (!estimate) return null;

  const handleScopePack = async (options = {}) => {
    const sel = estimate.selected_components || [];
    const selectedNames = new Set(
      Array.isArray(sel)
        ? sel.map(c => c.component_name || c.name).filter(Boolean)
        : Object.keys(sel)
    );
    if (selectedNames.size === 0) { toast.error('No components selected'); return; }
    if (allComponents.length === 0) { toast.error('Component catalog still loading'); return; }
    setScopeExporting(true);
    try {
      const selected = allComponents.filter(c => selectedNames.has(c.name));
      const filename = exportScopePack({
        organizationName: estimate.organization_name || estimate.org_name || 'Client',
        implementationName: estimate.implementation_name || estimate.impl_name,
        implementationType: estimate.implementation_type,
        complexity: estimate.complexity,
        totalEffort: estimate.results?.total_effort,
        totalCost: estimate.results?.total_cost,
        currencySymbol: getCurrencySymbol(estimate?.currency_code),
        preparedBy: user ? { name: user.name || '', email: user.email || '' } : null,
      }, selected, options);
      toast.success(`Exported ${filename}`);
    } catch (e) {
      toast.error('Scope pack export failed');
    } finally {
      setScopeExporting(false);
    }
  };

  const selectedCount = Array.isArray(estimate.selected_components)
    ? estimate.selected_components.length
    : Object.keys(estimate.selected_components || {}).length;

  const results = estimate.results || {};
  const componentBreakdown = results.component_breakdown || [];
  const modifiers = estimate.modifiers || {};

  // Group components using breakdown data (has group info)
  const groups = {};
  componentBreakdown.forEach(c => {
    const g = c.group || 'Other';
    if (!groups[g]) groups[g] = [];
    groups[g].push(c);
  });

  // Role breakdown
  const effortByRole = results.effort_by_role || {};
  const costByRole = results.cost_by_role || {};

  // Resolve currency code for display (stakeholders always need the
  // currency context when dollar amounts appear). Cascade mirrors
  // EstimatorPage: estimate override > user preference > USD.
  const currencyCode = (
    estimate.currency_code
    || user?.preferences?.default_currency_code
    || 'USD'
  );
  const currencySymbol = getCurrencySymbol(currencyCode);

  const ROLE_LABELS = {
    PM: 'Project Mgmt', CS: 'Consulting', CF: 'Configuration',
    IN: 'Integration', DM: 'Data Migration', DE: 'Development', IF: 'Infrastructure',
    UX: 'UX', TE: 'Testing'
  };

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8 py-6 flex gap-6" data-testid="workspace-overview">
      <div className="flex-1 min-w-0 space-y-6">
      {/* Scope Pack: Download + Email (hidden from view-only users) */}
      <div className="flex justify-end gap-2">
        {user?.role !== 'user' && (
        <>
        <Button size="sm" variant="outline" onClick={() => setEmailDialogOpen(true)}
          disabled={allComponents.length === 0 || selectedCount === 0}
          className="h-8 text-xs border-[#0F2040] text-[#0F2040] hover:bg-[#0F2040]/5"
          data-testid="overview-email-scope-pack-btn"
          title="Email the Scope Pack PDF directly to stakeholders">
          <Mail className="w-3.5 h-3.5 mr-1.5" />
          Email Scope Pack
        </Button>
        <Button size="sm" onClick={() => setScopeDialogOpen(true)} disabled={scopeExporting || allComponents.length === 0}
          className="bg-[#0F2040] hover:bg-[#1A3055] text-white h-8 text-xs"
          data-testid="overview-scope-pack-btn"
          title="Download a branded PDF booklet with a cover page + one factsheet per selected component">
          {scopeExporting
            ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
            : <Download className="w-3.5 h-3.5 mr-1.5" />}
          Download Scope Pack
        </Button>
        </>
        )}
        <Button size="sm" onClick={() => setRfpDialogOpen(true)} disabled={rfpExporting}
          className="bg-[#4ABFCF] hover:bg-[#3AA9B9] text-[#0F2040] h-8 text-xs font-semibold"
          data-testid="overview-rfp-pack-btn"
          title="Download a Seertech-branded Word document with AI project description, phases, effort, resources, timeline, risks and cost — ready to paste into RFP responses">
          {rfpExporting
            ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
            : <Download className="w-3.5 h-3.5 mr-1.5" />}
          Download RFP Support Pack
        </Button>
      </div>

      <Dialog open={rfpDialogOpen} onOpenChange={setRfpDialogOpen}>
        <DialogContent className="max-w-xl w-[95vw] sm:w-full bg-white overflow-x-hidden max-h-[85vh] overflow-y-auto [&>*]:min-w-0 [&>*]:max-w-full" data-testid="rfp-pack-dialog">
          <DialogHeader>
            <DialogTitle>RFP Support Pack</DialogTitle>
            <DialogDescription>
              A Seertech-branded Word document with an AI-written project description, phases,
              effort, resource allocation, timeline (with charts), risk areas and detail
              appendices — ready to copy into RFP responses.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2.5">
            <div>
              <Label htmlFor="rfp-include-costs" className="text-sm font-medium text-[#0F2040]">Include cost sections</Label>
              <p className="text-xs text-slate-500 mt-0.5">Section 7 (cost summary, billing timeline) + cost appendix</p>
            </div>
            <Switch id="rfp-include-costs" checked={rfpIncludeCosts} onCheckedChange={setRfpIncludeCosts}
              data-testid="rfp-include-costs-toggle" />
          </div>
          <div className="rounded-md border border-slate-200 px-3 py-2.5">
            <div className="flex items-center justify-between mb-1.5">
              <Label className="text-sm font-medium text-[#0F2040]">Include boilerplate blocks</Label>
              <a href="/rfp-library" target="_blank" rel="noreferrer"
                className="text-[10px] text-[#4ABFCF] hover:text-[#0F2040] font-medium underline underline-offset-2"
                data-testid="rfp-library-link">Browse library</a>
            </div>
            {packTemplates.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2" data-testid="rfp-pack-templates">
                {packTemplates.map(t => (
                  <span key={t.id} className="inline-flex items-center gap-1 text-[10px] bg-[#4ABFCF]/10 text-[#0F2040] border border-[#4ABFCF]/40 rounded-full pl-2 pr-1 py-0.5">
                    <button onClick={() => setRfpSelectedBlocks(t.block_ids.filter(id => (rfpBlocks || []).some(b => b.id === id)))}
                      className="font-medium hover:underline" title={`Apply "${t.name}" (${t.block_ids.length} blocks)`}
                      data-testid={`rfp-tpl-apply-${t.id}`}>{t.name}</button>
                    <button onClick={async () => {
                      if (!window.confirm(`Delete template "${t.name}"?`)) return;
                      try {
                        await axios.delete(`${API_URL}/api/rfp/pack-templates/${t.id}`, { withCredentials: true });
                        setPackTemplates(prev => prev.filter(x => x.id !== t.id));
                      } catch (err) { toast.error(err?.response?.data?.detail || 'Could not delete template'); }
                    }} className="text-slate-400 hover:text-red-600" data-testid={`rfp-tpl-delete-${t.id}`} title="Delete template">×</button>
                  </span>
                ))}
              </div>
            )}
            <p className="text-xs text-slate-500 mb-2">
              Selected blocks are appended as a "Standard Response Content" section
              {Array.isArray(rfpBlocks) && rfpBlocks.length > 0 ? ` · ${rfpBlocks.length} blocks — scroll for more` : ''}
            </p>
            {rfpBlocks === null ? (
              <p className="text-xs text-slate-400">Loading library…</p>
            ) : rfpBlocks.length === 0 ? (
              <p className="text-xs text-slate-400">No blocks in the library yet</p>
            ) : (
              <div className="max-h-40 overflow-y-auto space-y-1 pr-2" data-testid="rfp-boilerplate-picker">
                {rfpBlocks.map(b => (
                  <label key={b.id} className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer hover:bg-slate-50 rounded px-1 py-0.5">
                    <input type="checkbox" className="accent-[#0F2040]" checked={rfpSelectedBlocks.includes(b.id)}
                      onChange={() => toggleRfpBlock(b.id)} data-testid={`rfp-bp-check-${b.id}`} />
                    <span className="truncate flex-1">
                      {b.capability_id ? <span className="font-mono text-[9px] text-slate-400 mr-1">{b.capability_id}</span> : null}
                      {b.title}
                    </span>
                    <span className="text-[9px] text-slate-400 shrink-0 max-w-[140px] truncate"
                      title={(b.category_names || []).join(', ') || b.category}>{(b.category_names || []).join(', ') || b.category}</span>
                  </label>
                ))}
              </div>
            )}
            {rfpSelectedBlocks.length > 0 && (
              <div className="flex items-center gap-1.5 mt-2" data-testid="rfp-tpl-save-row">
                <input value={tplName} onChange={e => setTplName(e.target.value)}
                  placeholder={`Save these ${rfpSelectedBlocks.length} as template…`}
                  className="text-[10px] border border-dashed border-slate-300 rounded-full px-2.5 py-1 flex-1 focus:outline-none focus:border-[#4ABFCF]"
                  data-testid="rfp-tpl-name-input" />
                <button onClick={async () => {
                  if (!tplName.trim()) { toast.error('Give the template a name'); return; }
                  setTplSaving(true);
                  try {
                    const res = await axios.post(`${API_URL}/api/rfp/pack-templates`,
                      { name: tplName.trim(), block_ids: rfpSelectedBlocks }, { withCredentials: true });
                    setPackTemplates(prev => [...prev, res.data].sort((a, b) => a.name.localeCompare(b.name)));
                    setTplName('');
                    toast.success(`Template "${res.data.name}" saved`);
                  } catch (err) { toast.error(err?.response?.data?.detail || 'Could not save template'); }
                  finally { setTplSaving(false); }
                }} disabled={tplSaving}
                  className="text-[10px] font-medium text-white bg-[#0F2040] hover:bg-[#1A3055] rounded-full px-3 py-1 disabled:opacity-50"
                  data-testid="rfp-tpl-save-btn">
                  {tplSaving ? 'Saving…' : 'Save as template'}
                </button>
              </div>
            )}
          </div>
          <div className="rounded-md border border-slate-200 px-3 py-2.5 space-y-2">
            <Label className="text-sm font-medium text-[#0F2040]">Cover page details</Label>
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="Client name" value={rfpCover.client_name}
                onChange={e => setRfpCover(c => ({ ...c, client_name: e.target.value }))}
                className="h-8 text-xs" data-testid="rfp-cover-client-input" />
              <Input placeholder="RFP reference (e.g. RFP-2026-014)" value={rfpCover.rfp_reference}
                onChange={e => setRfpCover(c => ({ ...c, rfp_reference: e.target.value }))}
                className="h-8 text-xs" data-testid="rfp-cover-ref-input" />
            </div>
            <Input placeholder="Rep contact (name · email)" value={rfpCover.rep_contact}
              onChange={e => setRfpCover(c => ({ ...c, rep_contact: e.target.value }))}
              className="h-8 text-xs" data-testid="rfp-cover-contact-input" />
          </div>
          <div className="rounded-md border border-slate-200 px-3 py-2.5">
            <button type="button" onClick={() => setRfpEmailOpen(o => !o)}
              className="text-sm font-medium text-[#0F2040] w-full text-left flex items-center justify-between"
              data-testid="rfp-email-toggle">
              Email to a colleague
              <span className="text-xs text-[#4ABFCF]">{rfpEmailOpen ? 'Hide' : 'Show'}</span>
            </button>
            {rfpEmailOpen && (
              <div className="mt-2 space-y-2" data-testid="rfp-email-section">
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder="Recipient email *" type="email" value={rfpEmail.recipient_email}
                    onChange={e => setRfpEmail(v => ({ ...v, recipient_email: e.target.value }))}
                    className="h-8 text-xs" data-testid="rfp-email-recipient-input" />
                  <Input placeholder="Recipient name" value={rfpEmail.recipient_name}
                    onChange={e => setRfpEmail(v => ({ ...v, recipient_name: e.target.value }))}
                    className="h-8 text-xs" data-testid="rfp-email-name-input" />
                </div>
                <Textarea placeholder="Optional message" rows={2} value={rfpEmail.message}
                  onChange={e => setRfpEmail(v => ({ ...v, message: e.target.value }))}
                  className="text-xs" data-testid="rfp-email-message-input" />
                <Button size="sm" onClick={handleRfpEmail} disabled={rfpEmailing || !rfpEmail.recipient_email}
                  className="bg-[#4ABFCF] hover:bg-[#3AA9B9] text-[#0F2040] font-semibold w-full h-8"
                  data-testid="rfp-email-send-btn">
                  {rfpEmailing ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null}
                  {rfpEmailing ? 'Sending…' : 'Send Email with Pack Attached'}
                </Button>
              </div>
            )}
            {Array.isArray(rfpSends) && rfpSends.length > 0 && (
              <div className="mt-2 pt-2 border-t border-slate-100" data-testid="rfp-send-history">
                <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide mb-1">Send history</p>
                <div className="space-y-0.5 max-h-20 overflow-y-auto">
                  {rfpSends.map(s => (
                    <p key={s.id} className="text-[10px] text-slate-500 truncate" data-testid={`rfp-send-${s.id}`}>
                      Sent to <span className="font-medium text-[#0F2040]">{s.recipient_name || s.recipient_email}</span>
                      {' '}on {s.sent_at ? new Date(s.sent_at).toLocaleDateString() : '—'}
                      {s.sent_by_user_email ? ` · by ${s.sent_by_user_email}` : ''}
                    </p>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setRfpDialogOpen(false)} data-testid="rfp-pack-cancel-btn">Cancel</Button>
            <Button size="sm" onClick={handleRfpPack} className="bg-[#0F2040] hover:bg-[#1A3055] text-white"
              data-testid="rfp-pack-download-btn">
              <Download className="w-3.5 h-3.5 mr-1.5" />
              Download DOCX
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ScopePackOptionsDialog
        open={scopeDialogOpen}
        onOpenChange={setScopeDialogOpen}
        estimate={estimate}
        componentCount={selectedCount}
        onExport={handleScopePack}
      />

      <EmailScopePackDialog
        open={emailDialogOpen}
        onOpenChange={setEmailDialogOpen}
        estimate={estimate}
        components={allComponents.filter(c => {
          const sel = estimate.selected_components || [];
          const names = new Set(Array.isArray(sel) ? sel.map(x => x.component_name || x.name).filter(Boolean) : Object.keys(sel));
          return names.has(c.name);
        })}
        preparedBy={user ? { name: user.name || '', email: user.email || '' } : null}
        onSent={() => setSendHistoryKey(k => k + 1)}
      />

      {/* Scope Pack send history (shown only after at least one send) */}
      <ScopePackSendHistory estimateId={estimateId} refreshKey={sendHistoryKey} />

      {/* Stakeholder feedback summary (shown only after at least one submission) */}
      <StakeholderFeedbackSummary estimateId={estimateId} refreshKey={sendHistoryKey}
        onEstimateMutated={reloadEstimate} />

      {/* Quick Actions — Compare/Quote hidden from the view-only USER role */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {user?.role !== 'user' && (
        <button
          onClick={() => navigate(`/workspace/${estimateId}/compare`)}
          className="flex items-center gap-3 p-3 bg-white rounded-lg border border-slate-200 hover:border-[#4ABFCF]/40 hover:shadow-sm transition-all text-left group"
          data-testid="quick-action-compare"
        >
          <div className="w-8 h-8 rounded-md bg-blue-50 flex items-center justify-center shrink-0">
            <GitCompareArrows className="w-4 h-4 text-blue-600" />
          </div>
          <div className="min-w-0">
            <p className="text-xs font-semibold text-[#0F2040]">Compare</p>
            <p className="text-[10px] text-slate-500">Side-by-side with another estimate</p>
          </div>
          <ArrowRight className="w-3.5 h-3.5 text-slate-300 ml-auto group-hover:text-[#4ABFCF] transition-colors" />
        </button>
        )}
        <button
          onClick={() => navigate(`/workspace/${estimateId}/planner`)}
          className="flex items-center gap-3 p-3 bg-white rounded-lg border border-slate-200 hover:border-[#4ABFCF]/40 hover:shadow-sm transition-all text-left group"
          data-testid="quick-action-planner"
        >
          <div className="w-8 h-8 rounded-md bg-teal-50 flex items-center justify-center shrink-0">
            <CalendarDays className="w-4 h-4 text-teal-600" />
          </div>
          <div className="min-w-0">
            <p className="text-xs font-semibold text-[#0F2040]">Plan</p>
            <p className="text-[10px] text-slate-500">Generate timeline & resources</p>
          </div>
          <ArrowRight className="w-3.5 h-3.5 text-slate-300 ml-auto group-hover:text-[#4ABFCF] transition-colors" />
        </button>
        {user?.role !== 'user' && (
        <button
          onClick={() => navigate(`/workspace/${estimateId}/quote`)}
          className="flex items-center gap-3 p-3 bg-white rounded-lg border border-slate-200 hover:border-[#4ABFCF]/40 hover:shadow-sm transition-all text-left group"
          data-testid="quick-action-quote"
        >
          <div className="w-8 h-8 rounded-md bg-violet-50 flex items-center justify-center shrink-0">
            <Briefcase className="w-4 h-4 text-violet-600" />
          </div>
          <div className="min-w-0">
            <p className="text-xs font-semibold text-[#0F2040]">Quote</p>
            <p className="text-[10px] text-slate-500">Preview & export the quote</p>
          </div>
          <ArrowRight className="w-3.5 h-3.5 text-slate-300 ml-auto group-hover:text-[#4ABFCF] transition-colors" />
        </button>
        )}
      </div>

      {user?.role !== 'user' && <DealCloseDateCard estimateId={estimateId} estimate={estimate} canEdit={user?.role !== 'user'} onSaved={() => reloadEstimate?.()} />}

      {/* Key Metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="border-slate-200">
          <CardContent className="p-4 text-center">
            <div className="flex items-center justify-center gap-1 text-slate-400 text-[10px] mb-1">
              <Clock className="w-3 h-3" />
              Total Effort
            </div>
            <p className="text-xl font-bold text-[#0F2040] font-['Outfit']" data-testid="overview-total-effort">
              {results.total_effort?.toLocaleString() || 0}
            </p>
            <p className="text-[10px] text-slate-400">hours</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4 text-center">
            <div className="flex items-center justify-center gap-1 text-slate-400 text-[10px] mb-1">
              <DollarSign className="w-3 h-3" />
              Total Cost
            </div>
            <p className="text-xl font-bold text-[#4ABFCF] font-['Outfit']" data-testid="overview-total-cost">
              <span className="text-[10px] font-semibold text-[#4ABFCF]/70 mr-1 align-middle tracking-wide" data-testid="overview-total-cost-currency">{currencyCode}</span>
              {currencySymbol}{results.total_cost?.toLocaleString() || 0}
            </p>
            <p className="text-[10px] text-slate-400">estimated</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4 text-center">
            <div className="flex items-center justify-center gap-1 text-slate-400 text-[10px] mb-1">
              <Shield className="w-3 h-3" />
              Confidence
            </div>
            <p className="text-xl font-bold text-[#0F2040] font-['Outfit']" data-testid="overview-confidence">
              {results.confidence || 'N/A'}
            </p>
            <p className="text-[10px] text-slate-400">score</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4 text-center">
            <div className="flex items-center justify-center gap-1 text-slate-400 text-[10px] mb-1">
              <Layers className="w-3 h-3" />
              Components
            </div>
            <p className="text-xl font-bold text-[#0F2040] font-['Outfit']" data-testid="overview-component-count">
              {componentBreakdown.length}
            </p>
            <p className="text-[10px] text-slate-400">selected</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Component Groups */}
        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold text-[#0F2040]">Components by Group</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {Object.entries(groups).map(([groupName, items]) => {
              const IconComp = GROUP_ICONS[groupName] || Layers;
              const colorClass = GROUP_COLORS[groupName] || 'bg-slate-50 text-slate-700 border-slate-200';
              const groupEffort = items.reduce((sum, c) => sum + (c.final_effort || c.base_hours || 0), 0);
              return (
                <div key={groupName} className={`p-3 rounded-lg border ${colorClass}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <IconComp className="w-3.5 h-3.5" />
                      <span className="text-xs font-medium">{groupName}</span>
                      <Badge variant="secondary" className="text-[9px] py-0 px-1.5">{items.length}</Badge>
                    </div>
                    <span className="text-xs font-semibold">{groupEffort.toLocaleString()} hrs</span>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {items.map(c => (
                      <span key={c.name || c.component_name} className="text-[9px] bg-white/60 rounded px-1.5 py-0.5 border border-current/10">
                        {c.name || c.component_name}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>

        {/* Right column */}
        <div className="space-y-6">
          {/* Role Breakdown */}
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold text-[#0F2040] flex items-center justify-between gap-2">
                <span>Effort by Role</span>
                <span
                  className="text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500 border border-slate-200"
                  data-testid="overview-role-cost-currency"
                  title={`Cost figures shown in ${currencyCode}`}
                >
                  Cost · {currencyCode}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {Object.entries(effortByRole)
                  .sort((a, b) => b[1] - a[1])
                  .map(([role, hours]) => {
                    const pct = results.total_effort ? ((hours / results.total_effort) * 100).toFixed(0) : 0;
                    const cost = costByRole[role] || 0;
                    return (
                      <div key={role} className="flex items-center gap-3">
                        <div className="w-16 text-[10px] font-medium text-slate-600 shrink-0">
                          {ROLE_LABELS[role] || role}
                        </div>
                        <div className="flex-1 h-5 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-[#0F2040]/80 rounded-full transition-all"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <div className="text-[10px] text-slate-600 w-20 text-right shrink-0">
                          {hours.toLocaleString()} hrs
                        </div>
                        <div className="text-[10px] text-slate-400 w-20 text-right shrink-0">
                          {currencySymbol}{cost.toLocaleString()}
                        </div>
                      </div>
                    );
                  })}
              </div>
            </CardContent>
          </Card>

          {/* Modifiers */}
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold text-[#0F2040]">Project Modifiers</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(modifiers).map(([key, value]) => {
                  if (typeof value !== 'number' && typeof value !== 'string') return null;
                  const label = MODIFIER_LABELS[key] || key.replace(/_/g, ' ');
                  const numVal = typeof value === 'number' ? value : parseFloat(value);
                  const isGood = numVal <= 1.0;
                  return (
                    <div key={key} className="flex items-center justify-between p-2 bg-slate-50 rounded-md">
                      <span className="text-[10px] text-slate-600">{label}</span>
                      <Badge
                        variant="outline"
                        className={`text-[9px] ${isGood ? 'border-emerald-300 text-emerald-700' : 'border-amber-300 text-amber-700'}`}
                      >
                        {numVal?.toFixed ? numVal.toFixed(1) : value}x
                      </Badge>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Estimate Info */}
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold text-[#0F2040]">Estimate Details</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="space-y-2 text-xs">
                {estimate.organization_name && (
                  <div className="flex justify-between">
                    <dt className="text-slate-500">Organization</dt>
                    <dd className="font-medium text-[#0F2040]">{estimate.organization_name}</dd>
                  </div>
                )}
                <div className="flex justify-between">
                  <dt className="text-slate-500">Type</dt>
                  <dd className="font-medium text-[#0F2040]">{estimate.implementation_type?.replace('_', ' ')}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-slate-500">Complexity</dt>
                  <dd className="font-medium text-[#0F2040]">{results.complexity_label || estimate.complexity}</dd>
                </div>
                {estimate.hosting_model && (
                  <div className="flex justify-between">
                    <dt className="text-slate-500">Hosting</dt>
                    <dd className="font-medium text-[#0F2040]">{estimate.hosting_model}</dd>
                  </div>
                )}
                <div className="flex justify-between">
                  <dt className="text-slate-500">Created</dt>
                  <dd className="font-medium text-[#0F2040]">
                    {estimate.created_at
                      ? new Date(estimate.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
                      : 'N/A'}
                  </dd>
                </div>
              </dl>
            </CardContent>
          </Card>
        </div>
      </div>
      </div>
      <LiveKPIRail
        storageKey="output"
        title="Live KPIs"
        sourceLabel={estimate.estimate_name || estimate.implementation_name || estimate.organization_name || 'Estimate'}
        metrics={kpiMetrics}
      />
    </div>
  );
}
